import { useState } from 'react';
import { Button } from '../buttons/Button';
import Divider from '../Divider';
import { CircleX, Settings } from '../icons';
import { Checkbox } from '../inputs/Checkbox';
import { RenderWhen } from '../RenderWhen';
import { Typography } from '../Typography';
import { Card } from './Card';
import { flattenClassNames } from '@trukt/portal-utils/flattenClassNames';

/**
 * Data structure to interact with the component
 */
export type ColumnGroups = { label: string; columns: string[] }[];

/**
 * Expected state for the column visibility
 */
export type VisibilityState = Record<string, boolean>;

export interface ColumnsSettingsCardProps {
  /**
   * Diccionary for mapping column's identifier names
   */
  dictionary: Record<string, string>;
  /**
   * Column groups to be displayed
   */
  columnGroups: ColumnGroups;
  /**
   * Column visibility state. Decides if a column is visible or not
   */
  columnVisibility: VisibilityState;
  /**
   * New state calculation whenver a checkbox is clicked
   * @param visibility
   * @returns
   */
  onVisibilityChange: (visibility: VisibilityState) => void;
  className?: {
    cardRoot?: string;
    groupContainer?: string;
  };
}

/**
 * Handles the visibility of the columns in a table
 */
export const ColumnSettingsCard = ({
  dictionary,
  columnGroups,
  columnVisibility,
  onVisibilityChange,
  className,
}: ColumnsSettingsCardProps) => {
  const [isVisible, setIsVisible] = useState(false);

  const handleColumnVisibilityChange = (visibility: VisibilityState) => {
    onVisibilityChange(visibility);
  };

  const handleGroupVisibilityChange = (groupLabel: string) => () => {
    const groupColumns = columnGroups.find((g) => g.label === groupLabel)?.columns ?? [];
    const groupVisibility = groupColumns.every((accessor) => columnVisibility[accessor]);

    const newVisibility = groupColumns.reduce<VisibilityState>((acc, accessor) => {
      acc[accessor] = !groupVisibility;
      return acc;
    }, {});

    handleColumnVisibilityChange({ ...columnVisibility, ...newVisibility });
  };

  const toggleVisibility = () => {
    setIsVisible((prev) => !prev);
  };

  return (
    <>
      <Button
        variant="transparent"
        className="!flex size-9 items-center justify-center !rounded-full bg-primary-400"
        onClick={toggleVisibility}
      >
        <Settings
          role="img"
          aria-label="Table Settings"
          className="fill-common-white stroke-primary-400 hover:animate-spin"
        >
          <title>Table Settings</title>
        </Settings>
      </Button>
      <RenderWhen condition={isVisible}>
        <Card className={flattenClassNames(['absolute top-14 right-5 z-30', className?.cardRoot])}>
          <Card.Content>
            <div className="flex items-center justify-between pb-4">
              <Typography className="!text-h2">Configure Columns</Typography>
              <Button variant="text" onClick={toggleVisibility}>
                <CircleX
                  role="img"
                  aria-label="Close"
                  className="cursor-pointer stroke-primary-400"
                >
                  <title>Close</title>
                </CircleX>
              </Button>
            </div>

            <div>
              {columnGroups.map((group, groupIndex) => (
                <div className="pb-2" key={group.label}>
                  <div className="flex items-center gap-2 pb-2">
                    <Checkbox
                      value={group.columns.every((accessor) => columnVisibility[accessor])}
                      onClick={handleGroupVisibilityChange(group.label)}
                    />
                    <Typography className="!text-h4">{group.label}</Typography>
                  </div>

                  <div className={['grid grid-cols-4 gap-2', className?.groupContainer].join(' ')}>
                    {group.columns.map((accessor) => (
                      <Checkbox
                        value={columnVisibility[accessor]}
                        onClick={() => {
                          handleColumnVisibilityChange({
                            ...columnVisibility,
                            [accessor]: !columnVisibility[accessor],
                          });
                        }}
                        label={dictionary[accessor] ?? ''}
                        key={accessor}
                      />
                    ))}
                  </div>
                  <RenderWhen condition={groupIndex < columnGroups.length - 1}>
                    <Divider className="pt-2" />
                  </RenderWhen>
                </div>
              ))}
            </div>
          </Card.Content>
        </Card>
      </RenderWhen>
    </>
  );
};
